// src/components/analytics/FinancialAnalytics.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  DollarSign, TrendingUp, TrendingDown, Fuel, Plane,
  FileText, AlertCircle, CheckCircle, Clock, Building2,
  RefreshCw, ArrowUpRight, ArrowDownLeft, BarChart2,
  PieChart, Users, Activity, CreditCard, Receipt,
  Ban, Play, Link, Send
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// ─── CSRF helper ─────────────────────────────────────────────────────────────
const getCsrfToken = () =>
  document.cookie.split('; ').find(r => r.startsWith('csrf_token='))?.split('=')[1] || '';

const csrfHeaders = () => ({ 'X-CSRF-Token': getCsrfToken() });

// ─── SVG helpers (même style que TakeoffLandingAnalytics) ────────────────────

const SparkLine = ({ data = [], color = '#38bdf8', height = 40, width = 180 }) => {
  if (!data.length) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 6) - 3;
    return `${x},${y}`;
  });
  const id = `sg${color.replace(/[^a-z0-9]/gi, '')}`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,${height} ${pts.join(' ')} ${width},${height}`} fill={`url(#${id})`} />
      <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
};

// P&L bar + line chart
const PLChart = ({ data = [] }) => {
  if (!data.length) return <div className="h-48 flex items-center justify-center text-gray-600 text-sm">No data</div>;
  const maxVal = Math.max(...data.flatMap(d => [d.revenue, d.expenses]), 1);
  const W = 600, H = 140, pad = 28, barW = Math.max(4, (W - pad * 2) / (data.length * 2.6));
  const marginPts = data.map((d, i) => {
    const x = pad + i * ((W - pad * 2) / data.length) + barW;
    const y = d.margin >= 0
      ? H - (Math.min(d.margin, 100) / 100) * (H - 10) - 5
      : H - 5;
    return `${x},${y}`;
  });

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H + 30}`} className="overflow-visible">
      {[0, 0.25, 0.5, 0.75, 1].map(t => (
        <g key={t}>
          <line x1={pad} y1={H * (1 - t)} x2={W - pad} y2={H * (1 - t)}
            stroke="#1e293b" strokeWidth="1" />
          <text x={pad - 4} y={H * (1 - t) + 4} textAnchor="end" fill="#475569" fontSize="8">
            ${Math.round(maxVal * t / 1000)}k
          </text>
        </g>
      ))}
      {data.map((d, i) => {
        const x = pad + i * ((W - pad * 2) / data.length);
        const hR = (d.revenue  / maxVal) * H;
        const hE = (d.expenses / maxVal) * H;
        return (
          <g key={i}>
            <rect x={x} y={H - hR} width={barW} height={hR}
              fill="#38bdf8" rx="2" opacity="0.85" />
            <rect x={x + barW + 2} y={H - hE} width={barW} height={hE}
              fill="#f87171" rx="2" opacity="0.75" />
            <text x={x + barW} y={H + 14} textAnchor="middle" fill="#475569" fontSize="7">
              {d.label}
            </text>
          </g>
        );
      })}
      {/* Margin line */}
      {marginPts.length > 1 && (
        <>
          <polyline points={marginPts.join(' ')} fill="none"
            stroke="#10b981" strokeWidth="1.5" strokeDasharray="3,2" strokeLinejoin="round" />
          {data.map((d, i) => {
            const x = pad + i * ((W - pad * 2) / data.length) + barW;
            const y = d.margin >= 0
              ? H - (Math.min(d.margin, 100) / 100) * (H - 10) - 5
              : H - 5;
            return (
              <circle key={i} cx={x} cy={y} r="2.5"
                fill="#10b981" stroke="#0f172a" strokeWidth="1" />
            );
          })}
        </>
      )}
    </svg>
  );
};

// Donut breakdown chart
const DonutBreakdown = ({ data = [], size = 150 }) => {
  const total = data.reduce((s, d) => s + d.amount, 0) || 1;
  const r = 52, circ = 2 * Math.PI * r;
  let offset = circ * 0.25;
  return (
    <svg width={size} height={size} viewBox="0 0 120 120">
      <circle cx="60" cy="60" r={r} fill="none" stroke="#1e293b" strokeWidth="16" />
      {data.map((d, i) => {
        const arc = (d.amount / total) * circ;
        const el = (
          <circle key={i} cx="60" cy="60" r={r} fill="none"
            stroke={d.color} strokeWidth="16"
            strokeDasharray={`${arc} ${circ}`}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dasharray 1s ease' }} />
        );
        offset -= arc;
        return el;
      })}
      <text x="60" y="56" textAnchor="middle" fill="white" fontSize="11" fontWeight="bold">
        ${Math.round(total / 1000)}k
      </text>
      <text x="60" y="68" textAnchor="middle" fill="#64748b" fontSize="7">TOTAL</text>
    </svg>
  );
};

// ─── KPI Card ────────────────────────────────────────────────────────────────

const KPICard = ({ title, value, sub, icon: Icon, color, trend, spark, format = 'currency' }) => {
  const fmt = (v) => {
    if (format === 'currency') return `$${Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    if (format === 'pct')      return `${v}%`;
    if (format === 'number')   return Number(v).toLocaleString();
    if (format === 'gallons')  return `${Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })} gal`;
    return v;
  };

  return (
    <div className="relative bg-gray-800/60 border border-gray-700/60 rounded-xl p-4 overflow-hidden group hover:border-gray-600 transition-all duration-300">
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: `radial-gradient(circle at 80% 20%, ${color}15 0%, transparent 60%)` }} />
      <div className="relative">
        <div className="flex items-start justify-between mb-1">
          <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">{title}</p>
          <div className="p-1.5 rounded-lg" style={{ background: `${color}20` }}>
            <Icon size={15} style={{ color }} />
          </div>
        </div>
        <p className="text-2xl font-bold tabular-nums mt-1" style={{ color }}>
          {fmt(value)}
        </p>
        {spark && (
          <div className="mt-1 opacity-60">
            <SparkLine data={spark} color={color} height={28} width={130} />
          </div>
        )}
        {sub && (
          <div className="flex items-center gap-1 mt-1.5">
            {trend === 'up'   && <TrendingUp  size={11} className="text-emerald-400" />}
            {trend === 'down' && <TrendingDown size={11} className="text-red-400" />}
            {trend === 'warn' && <AlertCircle  size={11} className="text-amber-400" />}
            <span className={`text-xs ${
              trend === 'up'   ? 'text-emerald-400' :
              trend === 'down' ? 'text-red-400'     :
              trend === 'warn' ? 'text-amber-400'   : 'text-gray-400'
            }`}>{sub}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Status badge ─────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const map = {
    paid:     'bg-emerald-500/20 text-emerald-400',
    pending:  'bg-amber-500/20   text-amber-400',
    sent:     'bg-sky-500/20     text-sky-400',
    overdue:  'bg-red-500/20     text-red-400',
    draft:    'bg-gray-500/20    text-gray-400',
    cancelled:'bg-gray-600/20   text-gray-500',
  };
  const cls = map[status?.toLowerCase()] || 'bg-gray-500/20 text-gray-400';
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {status || '—'}
    </span>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const FinancialAnalytics = () => {
  const [range,       setRange]       = useState('30d');
  const [loading,     setLoading]     = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [animated,    setAnimated]    = useState(false);

  const [kpi,        setKpi]        = useState({});
  const [plMonthly,  setPlMonthly]  = useState([]);
  const [breakdown,  setBreakdown]  = useState([]);
  const [topClients, setTopClients] = useState([]);
  const [recentTx,   setRecentTx]   = useState([]);

  // ── Movement Billing states ───────────────────────────────────────────────
  const [movLines,      setMovLines]      = useState([]);
  const [movSummary,    setMovSummary]    = useState(null);
  const [movLoading,    setMovLoading]    = useState(false);
  const [movFilter,     setMovFilter]     = useState('all');
  const [movRunning,    setMovRunning]    = useState(false);
  const [movToast,      setMovToast]      = useState(null);
  const [visitorModal,  setVisitorModal]  = useState(null); // { visit_id, tail }
  const [visitorEmail,  setVisitorEmail]  = useState('');
  const [visitorLink,   setVisitorLink]   = useState(null);
  const [genInvModal,   setGenInvModal]   = useState(null); // { owner_id, owner_name }
  const [genInvRunning, setGenInvRunning] = useState(false);

  const fmtCur = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

  // ── Load movement lines ───────────────────────────────────────────────────
  const loadMovLines = useCallback(async () => {
    setMovLoading(true);
    try {
      const [linesRes, summaryRes] = await Promise.all([
        axios.get(`${API_BASE}/billing/line-items`, { params: { limit: 200 }, withCredentials: true }),
        axios.get(`${API_BASE}/billing/line-items/summary`, { withCredentials: true }),
      ]);
      setMovLines(linesRes.data.lines || []);
      setMovSummary(summaryRes.data || {});
    } catch (err) {
      console.error('Movement billing error:', err);
    } finally {
      setMovLoading(false);
    }
  }, []);

  const handleExemptLine = async (lineId) => {
    try {
      await axios.patch(`${API_BASE}/billing/line-items/${lineId}/exempt`, {}, { withCredentials: true, headers: csrfHeaders() });
      setMovToast({ msg: 'Line marked as exempt', type: 'success' });
      await loadMovLines();
    } catch (err) {
      setMovToast({ msg: 'Error exempting line', type: 'error' });
    }
  };

  const handleRunMonthlyBilling = async () => {
    if (!window.confirm('Generate monthly invoices for ALL owners with pending lines and send via Stripe?')) return;
    setMovRunning(true);
    try {
      const res = await axios.post(`${API_BASE}/billing/run-monthly-billing`, {}, { withCredentials: true, headers: csrfHeaders() });
      setMovToast({ msg: `✅ ${res.data.owners_processed} owner(s) invoiced`, type: 'success' });
      await loadMovLines();
    } catch (err) {
      setMovToast({ msg: 'Error running monthly billing', type: 'error' });
    } finally {
      setMovRunning(false);
    }
  };

  const handleGenerateVisitorLink = async () => {
    if (!visitorModal) return;
    try {
      const res = await axios.post(`${API_BASE}/billing/visitor-payment-link`, {
        visit_id: visitorModal.visit_id,
        email: visitorEmail || null,
      }, { withCredentials: true, headers: csrfHeaders() });
      setVisitorLink(res.data.payment_url);
      setMovToast({ msg: '✅ Payment link generated', type: 'success' });
    } catch (err) {
      setMovToast({ msg: 'Error generating link', type: 'error' });
    }
  };

  const handleGenerateMonthlyForOwner = async () => {
    if (!genInvModal) return;
    setGenInvRunning(true);
    try {
      const res = await axios.post(`${API_BASE}/billing/generate-monthly`, {
        owner_id: genInvModal.owner_id,
      }, { withCredentials: true, headers: csrfHeaders() });
      setMovToast({ msg: `✅ Invoice ${res.data.invoice_number} created — ${fmtCur(res.data.total_amount)}`, type: 'success' });
      setGenInvModal(null);
      await loadMovLines();
    } catch (err) {
      setMovToast({ msg: err.response?.data?.detail || 'Error generating invoice', type: 'error' });
    } finally {
      setGenInvRunning(false);
    }
  };

  // Auto-hide toast
  useEffect(() => {
    if (movToast) { const t = setTimeout(() => setMovToast(null), 3500); return () => clearTimeout(t); }
  }, [movToast]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setAnimated(false);
    try {
      const res = await axios.get(`${API_BASE}/dashboard/financial-stats?range=${range}`);
      const d   = res.data;
      setKpi(d.kpi           || {});
      setPlMonthly(d.pl_monthly  || []);
      setBreakdown(d.breakdown   || []);
      setTopClients(d.top_clients || []);
      setRecentTx(d.recent_tx   || []);
    } catch (err) {
      console.error('Financial stats error:', err);
      // Fallback mock minimal
      setKpi({
        total_revenue: 0, total_expenses: 0, net_income: 0, net_margin_pct: 0,
        fuel_revenue: 0, fuel_gallons: 0, landing_fee_revenue: 0, lease_revenue: 0,
        active_leases: 0, pending_invoices: 0, pending_amount: 0,
        overdue_invoices: 0, overdue_amount: 0,
        supplier_overdue: 0, supplier_overdue_amount: 0,
      });
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
      setTimeout(() => setAnimated(true), 100);
    }
  }, [range]);

  useEffect(() => { fetchData(); loadMovLines(); }, [fetchData, loadMovLines]);

  const plSparkRevenue  = plMonthly.map(d => d.revenue);
  const plSparkExpenses = plMonthly.map(d => d.expenses);
  const maxClient = Math.max(...topClients.map(c => c.total), 1);

  const txTypeStyle = {
    invoice:  { icon: FileText,  color: '#38bdf8', label: 'Invoice' },
    fuel:     { icon: Fuel,      color: '#f59e0b', label: 'Fuel'    },
    supplier: { icon: Receipt,   color: '#f87171', label: 'Expense' },
  };

  return (
    <div className="space-y-4 px-1">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <span className="p-1.5 bg-emerald-500/20 rounded-lg inline-flex">
              <DollarSign size={18} className="text-emerald-400" />
            </span>
            Financial Analytics
          </h2>
          <p className="text-gray-400 text-xs mt-0.5 flex items-center gap-1">
            <Clock size={11} />
            Last refresh: {lastRefresh.toLocaleTimeString()} — P&amp;L · Revenue · Expenses
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-gray-800 border border-gray-700 rounded-lg overflow-hidden text-xs">
            {['7d', '30d', '90d', '12m'].map(r => (
              <button key={r} onClick={() => setRange(r)}
                className={`px-3 py-1.5 transition-colors ${range === r ? 'bg-emerald-600 text-white' : 'text-gray-400 hover:text-white'}`}>
                {r === '7d' ? '7 Days' : r === '30d' ? '30 Days' : r === '90d' ? '3 Months' : '12 Months'}
              </button>
            ))}
          </div>
          <button onClick={fetchData}
            className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 hover:text-white hover:border-gray-600 transition-colors">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* ── Row 1: KPI Cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPICard title="Total Revenue"   value={kpi.total_revenue  || 0} icon={TrendingUp}
          color="#10b981" trend="up"   sub="All sources combined"
          spark={plSparkRevenue} />
        <KPICard title="Total Expenses"  value={kpi.total_expenses || 0} icon={TrendingDown}
          color="#f87171" trend="down" sub="Supplier invoices"
          spark={plSparkExpenses} />
        <KPICard title="Net Income"      value={kpi.net_income     || 0} icon={DollarSign}
          color="#38bdf8" trend={kpi.net_income >= 0 ? 'up' : 'down'}
          sub={`Margin: ${kpi.net_margin_pct || 0}%`} />
        <KPICard title="Net Margin"      value={kpi.net_margin_pct || 0} icon={Activity}
          color="#a78bfa" format="pct"
          sub={kpi.net_income >= 0 ? 'Profitable' : 'Operating loss'}
          trend={kpi.net_margin_pct >= 0 ? 'up' : 'down'} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPICard title="Fuel Revenue"    value={kpi.fuel_revenue   || 0} icon={Fuel}
          color="#f59e0b" sub={`${kpi.fuel_gallons || 0} gal sold`} />
        <KPICard title="Landing Fees"    value={kpi.landing_fee_revenue || 0} icon={Plane}
          color="#818cf8" sub="Takeoffs & landings" />
        <KPICard title="Lease Revenue"   value={kpi.lease_revenue  || 0} icon={Building2}
          color="#34d399" sub={`${kpi.active_leases || 0} active leases`} />
        <KPICard title="Pending Invoices" value={kpi.pending_amount || 0} icon={Clock}
          color="#fbbf24" trend="warn"
          sub={`${kpi.pending_invoices || 0} invoices pending`} />
      </div>

      {/* Overdue alerts */}
      {((kpi.overdue_invoices || 0) > 0 || (kpi.supplier_overdue || 0) > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {(kpi.overdue_invoices || 0) > 0 && (
            <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/30 rounded-xl p-3">
              <AlertCircle size={18} className="text-red-400 flex-shrink-0" />
              <div>
                <p className="text-red-400 text-sm font-semibold">
                  {kpi.overdue_invoices} client invoice{kpi.overdue_invoices > 1 ? 's' : ''} overdue
                </p>
                <p className="text-red-300/70 text-xs">
                  ${Number(kpi.overdue_amount || 0).toLocaleString()} outstanding
                </p>
              </div>
            </div>
          )}
          {(kpi.supplier_overdue || 0) > 0 && (
            <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/30 rounded-xl p-3">
              <AlertCircle size={18} className="text-amber-400 flex-shrink-0" />
              <div>
                <p className="text-amber-400 text-sm font-semibold">
                  {kpi.supplier_overdue} supplier invoice{kpi.supplier_overdue > 1 ? 's' : ''} overdue
                </p>
                <p className="text-amber-300/70 text-xs">
                  ${Number(kpi.supplier_overdue_amount || 0).toLocaleString()} due
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Row 2: P&L Chart (full width) ── */}
      <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2">
            <BarChart2 size={15} className="text-emerald-400" />
            Profit &amp; Loss — Monthly (12 Months)
          </h3>
          <div className="flex items-center gap-4 text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <span className="w-3 h-2 rounded-sm bg-sky-400 inline-block opacity-85" />Revenue
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-2 rounded-sm bg-red-400 inline-block opacity-75" />Expenses
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-0.5 bg-emerald-400 inline-block" style={{ borderTop: '1px dashed' }} />Net Margin %
            </span>
          </div>
        </div>
        <PLChart data={plMonthly} />
        {/* Summary row */}
        <div className="grid grid-cols-3 gap-3 mt-4 pt-3 border-t border-gray-700">
          {['revenue', 'expenses', 'net'].map((key, i) => {
            const total  = plMonthly.reduce((s, d) => s + (d[key] || 0), 0);
            const labels = ['Total Revenue', 'Total Expenses', 'Net Income'];
            const colors = ['text-sky-400', 'text-red-400', total >= 0 ? 'text-emerald-400' : 'text-red-400'];
            return (
              <div key={key} className="text-center">
                <p className="text-gray-500 text-xs">{labels[i]}</p>
                <p className={`text-lg font-bold tabular-nums ${colors[i]}`}>
                  ${Math.abs(total / 1000).toFixed(1)}k
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Row 3: Breakdown + Top Clients ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">

        {/* Revenue breakdown donut */}
        <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl p-4">
          <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
            <PieChart size={15} className="text-purple-400" />
            Revenue by Source
          </h3>
          <div className="flex items-center justify-center mb-3">
            <DonutBreakdown data={breakdown} size={150} />
          </div>
          <div className="space-y-2">
            {breakdown.map(b => (
              <div key={b.source} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ background: b.color }} />
                  <span className="text-gray-300">{b.source}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-16 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700"
                      style={{
                        width: animated ? `${b.pct}%` : '0%',
                        background: b.color
                      }} />
                  </div>
                  <span className="text-gray-400 w-8 text-right">{b.pct}%</span>
                  <span className="font-semibold tabular-nums" style={{ color: b.color }}>
                    ${Number(b.amount).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top clients */}
        <div className="lg:col-span-2 bg-gray-800/60 border border-gray-700/60 rounded-xl p-4">
          <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
            <Users size={15} className="text-sky-400" />
            Top Clients by Revenue
          </h3>
          {topClients.length === 0 ? (
            <div className="text-center py-8 text-gray-600 text-sm">No invoice data for this period</div>
          ) : (
            <div className="space-y-3">
              {topClients.map((c, i) => (
                <div key={c.name} className="flex items-center gap-3">
                  <span className="text-gray-600 text-xs w-4 text-right flex-shrink-0">{i + 1}</span>
                  <span className="text-gray-300 text-xs flex-1 truncate">{c.name}</span>
                  <div className="flex-1 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-sky-400 rounded-full transition-all duration-700"
                      style={{ width: animated ? `${(c.total / maxClient) * 100}%` : '0%' }} />
                  </div>
                  <span className="text-sky-400 text-xs font-semibold tabular-nums w-20 text-right">
                    ${Number(c.total).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Row 4: Recent Transactions ── */}
      <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2">
            <CreditCard size={15} className="text-indigo-400" />
            Recent Transactions
          </h3>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-sky-400" />Invoices
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-amber-400" />Fuel
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-red-400" />Expenses
            </span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-500 border-b border-gray-700">
                <th className="text-left py-2 px-3">Type</th>
                <th className="text-left py-2 px-3">Reference</th>
                <th className="text-left py-2 px-3">Party</th>
                <th className="text-left py-2 px-3">Date</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-right py-2 px-3">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {recentTx.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-8 text-center text-gray-600">No transactions found</td>
                </tr>
              ) : recentTx.map((tx, i) => {
                const style  = txTypeStyle[tx.tx_type] || txTypeStyle.invoice;
                const TxIcon = style.icon;
                const isExpense = tx.tx_type === 'supplier';
                return (
                  <tr key={i} className="hover:bg-gray-700/30 transition-colors">
                    <td className="py-2 px-3">
                      <span className="flex items-center gap-1.5" style={{ color: style.color }}>
                        <TxIcon size={12} />
                        <span>{style.label}</span>
                      </span>
                    </td>
                    <td className="py-2 px-3 font-mono text-gray-300">{tx.reference || '—'}</td>
                    <td className="py-2 px-3 text-gray-300 max-w-[140px] truncate">
                      {tx.party || <span className="text-gray-600 italic">—</span>}
                    </td>
                    <td className="py-2 px-3 text-gray-400 tabular-nums">
                      {tx.tx_date
                        ? new Date(tx.tx_date).toLocaleDateString([], { month: 'short', day: 'numeric', year: '2-digit' })
                        : '—'}
                    </td>
                    <td className="py-2 px-3">
                      <StatusBadge status={tx.status} />
                    </td>
                    <td className={`py-2 px-3 text-right font-semibold tabular-nums ${
                      isExpense ? 'text-red-400' : 'text-emerald-400'
                    }`}>
                      {isExpense ? '-' : '+'}${Math.abs(tx.amount).toLocaleString('en-US', { maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Row 5: Movement Billing ── */}
      <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl p-4 space-y-4">

        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2">
            <Plane size={15} className="text-sky-400" />
            Movement Billing
            {movSummary?.pending && movSummary.pending.count > 0 && (
              <span className="bg-yellow-500 text-black text-xs rounded-full px-1.5 py-0.5 font-bold">
                {movSummary.pending.count} pending
              </span>
            )}
          </h3>
          <div className="flex items-center gap-2">
            <button onClick={loadMovLines}
              className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-400 hover:text-white transition">
              <RefreshCw size={13} className={movLoading ? 'animate-spin' : ''} />
            </button>
            <button onClick={handleRunMonthlyBilling} disabled={movRunning}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 text-blue-400 text-xs rounded-lg transition disabled:opacity-50">
              {movRunning
                ? <div className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                : <Play size={12} />}
              {movRunning ? 'Running...' : 'Run Monthly Billing'}
            </button>
          </div>
        </div>

        {/* Summary cards */}
        {movSummary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            {[
              { key: 'pending',         label: 'Pending',         color: 'text-yellow-400',  bg: 'bg-yellow-500/10  border-yellow-500/20'  },
              { key: 'visitor_pending', label: 'Visitor Pending',  color: 'text-purple-400',  bg: 'bg-purple-500/10  border-purple-500/20'  },
              { key: 'invoiced',        label: 'Invoiced',         color: 'text-blue-400',    bg: 'bg-blue-500/10    border-blue-500/20'    },
              { key: 'paid',            label: 'Paid',             color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
            ].map(({ key, label, color, bg }) => (
              <div key={key} className={`rounded-lg border p-2.5 ${bg}`}>
                <p className="text-gray-400 text-xs">{label}</p>
                <p className={`text-lg font-bold tabular-nums ${color}`}>{fmtCur(movSummary[key]?.total || 0)}</p>
                <p className="text-gray-600 text-xs">{movSummary[key]?.count || 0} line(s)</p>
              </div>
            ))}
          </div>
        )}

        {/* Filter tabs */}
        <div className="flex gap-1 flex-wrap">
          {['all', 'pending', 'visitor_pending', 'invoiced', 'paid', 'exempt'].map(s => (
            <button key={s} onClick={() => setMovFilter(s)}
              className={`px-2.5 py-1 rounded-lg text-xs transition ${
                movFilter === s
                  ? 'bg-sky-500/20 text-sky-400 border border-sky-500/30'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}>
              {s === 'all' ? 'All' : s === 'visitor_pending' ? 'Visitor' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        {/* Lines table */}
        <div className="overflow-x-auto rounded-lg border border-gray-700">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-500 bg-gray-800/80 border-b border-gray-700">
                <th className="text-left py-2 px-3">Tail</th>
                <th className="text-left py-2 px-3">Type</th>
                <th className="text-left py-2 px-3">Date</th>
                <th className="text-left py-2 px-3">Owner</th>
                <th className="text-right py-2 px-3">Fee</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-left py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {movLoading ? (
                <tr><td colSpan="7" className="py-8 text-center text-gray-500">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />Loading...
                  </div>
                </td></tr>
              ) : movLines.filter(l => movFilter === 'all' || l.status === movFilter).length === 0 ? (
                <tr><td colSpan="7" className="py-8 text-center text-gray-600">No lines found</td></tr>
              ) : (
                movLines.filter(l => movFilter === 'all' || l.status === movFilter).map(l => {
                  const statusCfg = {
                    pending:         'bg-yellow-500/20 text-yellow-400',
                    visitor_pending: 'bg-purple-500/20 text-purple-400',
                    invoiced:        'bg-blue-500/20   text-blue-400',
                    paid:            'bg-emerald-500/20 text-emerald-400',
                    exempt:          'bg-gray-500/20   text-gray-400',
                    stripe_pending:  'bg-indigo-500/20 text-indigo-400',
                  };
                  return (
                    <tr key={l.id} className="hover:bg-gray-700/20 transition-colors">
                      <td className="py-2 px-3">
                        <code className="text-sky-400 font-mono">{l.tail_number || '—'}</code>
                        {l.is_visitor && <span className="ml-1 text-purple-400">(visitor)</span>}
                      </td>
                      <td className="py-2 px-3">
                        <span className={`flex items-center gap-1 font-medium ${l.movement_type === 'TAKEOFF' ? 'text-blue-400' : 'text-amber-400'}`}>
                          {l.movement_type === 'TAKEOFF' ? <ArrowUpRight size={12} /> : <ArrowDownLeft size={12} />}
                          {l.movement_type === 'TAKEOFF' ? 'Kick-off' : 'Landing'}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-gray-400 tabular-nums">
                        {l.movement_date ? new Date(l.movement_date).toLocaleDateString([], { month: 'short', day: 'numeric' }) : '—'}
                      </td>
                      <td className="py-2 px-3 text-gray-300 max-w-[120px] truncate">
                        {l.owner_name || <span className="text-gray-600 italic">Unregistered</span>}
                      </td>
                      <td className={`py-2 px-3 text-right font-semibold tabular-nums ${parseFloat(l.total_amount) > 0 ? 'text-white' : 'text-gray-600'}`}>
                        {parseFloat(l.total_amount) > 0 ? fmtCur(l.total_amount) : 'Exempt'}
                      </td>
                      <td className="py-2 px-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusCfg[l.status] || 'bg-gray-500/20 text-gray-400'}`}>
                          {l.status === 'visitor_pending' ? 'Visitor' : l.status?.charAt(0).toUpperCase() + l.status?.slice(1)}
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-1">
                          {['pending', 'visitor_pending'].includes(l.status) && (
                            <button onClick={() => handleExemptLine(l.id)}
                              className="p-1 hover:bg-gray-600 rounded text-gray-500 hover:text-white transition" title="Mark exempt">
                              <Ban size={12} />
                            </button>
                          )}
                          {l.status === 'visitor_pending' && l.visit_id && (
                            <button onClick={() => { setVisitorModal({ visit_id: l.visit_id, tail: l.tail_number }); setVisitorLink(null); setVisitorEmail(''); }}
                              className="p-1 hover:bg-purple-500/20 rounded text-purple-400 transition" title="Generate payment link">
                              <Link size={12} />
                            </button>
                          )}
                          {l.status === 'pending' && l.owner_id && (
                            <button onClick={() => setGenInvModal({ owner_id: l.owner_id, owner_name: l.owner_name })}
                              className="p-1 hover:bg-blue-500/20 rounded text-blue-400 transition" title="Generate invoice">
                              <FileText size={12} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          {movLines.length > 0 && (
            <div className="px-3 py-2 border-t border-gray-700 flex justify-between text-xs text-gray-500">
              <span>{movLines.filter(l => movFilter === 'all' || l.status === movFilter).length} line(s)</span>
              <span>Total: <span className="text-white font-medium">
                {fmtCur(movLines.filter(l => movFilter === 'all' || l.status === movFilter).reduce((s, l) => s + parseFloat(l.total_amount || 0), 0))}
              </span></span>
            </div>
          )}
        </div>
      </div>

      {/* ── Modal: Visitor Payment Link ── */}
      {visitorModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                <Link size={15} className="text-purple-400" />
                Payment Link — {visitorModal.tail}
              </h3>
              <button onClick={() => setVisitorModal(null)} className="text-gray-400 hover:text-white">✕</button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs text-gray-400 block mb-1">Visitor email (optional)</label>
                <input type="email" value={visitorEmail} onChange={e => setVisitorEmail(e.target.value)}
                  placeholder="visitor@example.com"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" />
              </div>
              {visitorLink ? (
                <div className="space-y-2">
                  <p className="text-xs text-emerald-400 font-medium">✅ Link generated:</p>
                  <div className="flex items-center gap-2 bg-gray-900 rounded-lg p-2 border border-gray-700">
                    <code className="text-sky-400 text-xs flex-1 truncate">{visitorLink}</code>
                    <button onClick={() => { navigator.clipboard.writeText(visitorLink); setMovToast({ msg: 'Copied!', type: 'success' }); }}
                      className="text-gray-400 hover:text-white text-xs px-2 py-1 bg-gray-700 rounded">Copy</button>
                  </div>
                  <a href={visitorLink} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-purple-400 hover:underline">
                    <Send size={11} /> Open payment page
                  </a>
                </div>
              ) : (
                <button onClick={handleGenerateVisitorLink}
                  className="w-full py-2 bg-purple-500 hover:bg-purple-600 rounded-lg text-white text-sm font-medium transition flex items-center justify-center gap-2">
                  <Link size={14} /> Generate Stripe Payment Link
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Generate Monthly Invoice ── */}
      {genInvModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-sm border border-gray-700">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                <FileText size={15} className="text-blue-400" />
                Generate Monthly Invoice
              </h3>
              <button onClick={() => setGenInvModal(null)} className="text-gray-400 hover:text-white">✕</button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-gray-300 text-sm">
                Generate invoice for <span className="text-white font-medium">{genInvModal.owner_name}</span> and send via Stripe?
              </p>
              <p className="text-gray-500 text-xs">Groups all pending lines for the current month.</p>
              <div className="flex gap-2 pt-2">
                <button onClick={handleGenerateMonthlyForOwner} disabled={genInvRunning}
                  className="flex-1 py-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 rounded-lg text-white text-sm font-medium transition flex items-center justify-center gap-2">
                  {genInvRunning
                    ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : <CheckCircle size={14} />}
                  {genInvRunning ? 'Generating...' : 'Generate & Send'}
                </button>
                <button onClick={() => setGenInvModal(null)}
                  className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm transition">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast ── */}
      {movToast && (
        <div className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-xl text-sm font-medium shadow-lg border ${
          movToast.type === 'success'
            ? 'bg-emerald-900/90 border-emerald-500/50 text-emerald-300'
            : 'bg-red-900/90 border-red-500/50 text-red-300'
        }`}>
          {movToast.msg}
        </div>
      )}

    </div>
  );
};

export default FinancialAnalytics;